import React, { ReactNode } from 'react';
import styled from 'styled-components';
import ButtonOutline from '../../app/common/components/ButtonOutline';
import OverlayDialog from '../../app/common/components/OverlayDialog';
import Spinner from '../../app/common/components/Spinner';
import ICONS from '../../app/common/constants/icons';
import { COLORS, MQS } from '../../app/common/constants/styles';
import { IAddress } from '../addresses/address/address.interface';
import { TranslationHelpers } from '../../i18n/translationHelpers';
import { TooltipWrapper } from '../../app/common/components/TooltipWrapper';
import {
    destroyChallenge,
    startChallenge,
    updateChallengerLanguageWithOutStop,
    RenderChallenge,
} from '../../utils/challengerMain';
import { SupportedLanguage } from '../../config/constants/supportedLanguage.enum';
import { ICard } from './cards/card/card.interface';
import CardContainer from './cards/card/CardContainer';
import PaymentMethodFormContainer from './paymentMethodForm/PaymentMethodFormContainer';
import CardSharingDisclaimer from './CardSharingDisclaimer';
import OverlayHelper from '../../utils/overlayHelper';
import { PageId } from '../../config/constants/pageId.enum';
import HeadTitle from '../../app/common/components/HeadTitle';
import { addRatEvent } from '../../utils/ratClickEvents';

interface IPaymentMethodsProps {
    maxNumberOfCards: number;
    addresses: IAddress[];
    cards: ICard[];
    formOpen: boolean;
    loading: boolean;
    getCards: () => void;
    closeOverlay: () => void;
    openOverlay: () => void;
    showBillingAddress: boolean;
    locale: SupportedLanguage;
    challengerPid: string;
    brandCodes: string[];
    challengerPidDefault: string;
    showCardSharingDisclaimer: boolean;
    cardSharingServiceName: string;
    cardSharingMigrationDate: string;
}

interface IPaymentMethodsState {
    selectedCard?: ICard;
}

const PaymentMethodsWrapper = styled.div`
    & > button {
        padding: 15px 65px;

        @media (max-width: ${MQS.mq2Min}) {
            padding: 15px 30px;
        }
    }
    > h1 {
        margin-left: 10px;
    }
`;

const CardListWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: left;
    margin-bottom: 30px;

    @media (max-width: ${MQS.mq2Min}) {
        justify-content: center;
    }
`;

const CardWrapper = styled.div`
    margin: 10px;
`;

const SpinnerWrapper = styled.div`
    margin: 50px auto;
    width: 50px;
`;

const NoticeWrapper = styled.div`
    margin: 5px 10px 10px 10px;
`;

const NoticeParagraph = styled.p`
    margin: 0 0 5px 0;
    line-height: 1.5;
    font-size: 0.875rem;
    color: ${COLORS.dark1};

    &:last-child {
        margin-bottom: 0;
    }
`;

class PaymentMethods extends React.Component<IPaymentMethodsProps, IPaymentMethodsState> {
    constructor(props: IPaymentMethodsProps) {
        super(props);

        this.state = {};

        this.selectCard = this.selectCard.bind(this);
        this.createCard = this.createCard.bind(this);
    }

    public componentDidMount() {
        window.scrollTo({ top: 0 });
        const { getCards } = this.props;
        const { challengerPid, challengerPidDefault } = this.props;
        getCards();
        startChallenge(challengerPid);
        startChallenge(challengerPidDefault);
    }

    public componentWillUnmount() {
        const { challengerPid, challengerPidDefault } = this.props;
        destroyChallenge(challengerPid);
        destroyChallenge(challengerPidDefault);
        OverlayHelper.closeHelper(PageId.cards);
    }

    public componentDidUpdate(prevProps: IPaymentMethodsProps): void {
        const { locale } = this.props;
        if (prevProps.locale !== locale) {
            updateChallengerLanguageWithOutStop(locale);
        }
    }

    private renderChallenge(challengerPid: string) {
        return (
            <div>
                <RenderChallenge cpid={challengerPid} />
            </div>
        );
    }

    public render() {
        const {
            maxNumberOfCards,
            cards,
            loading,
            formOpen,
            closeOverlay,
            showBillingAddress,
            brandCodes,
            challengerPidDefault,
            showCardSharingDisclaimer,
            cardSharingServiceName,
            cardSharingMigrationDate,
        } = this.props;
        const { selectedCard } = this.state;
        let main;
        let addCardsButton;

        if (loading) {
            main = (
                <SpinnerWrapper>
                    <Spinner size={50} color={COLORS.white} />
                </SpinnerWrapper>
            );
            addCardsButton = '';
        } else {
            main = (
                <CardListWrapper>
                    {cards.map((card, index) => (
                        <CardWrapper key={`card-${index}`}>
                            <CardContainer
                                card={card}
                                onEdit={this.selectCard}
                                isOnlyCard={cards.length === 1}
                                qaId={`${index}`}
                                showBillingAddress={showBillingAddress}
                            />
                        </CardWrapper>
                    ))}
                    {challengerPidDefault && this.renderChallenge(challengerPidDefault)}
                </CardListWrapper>
            );
            addCardsButton = this.addCardsButton(cards.length, maxNumberOfCards);
        }

        return (
            <PaymentMethodsWrapper>
                <HeadTitle />
                <NoticeWrapper>
                    <NoticeParagraph>
                        {TranslationHelpers.getFormattedMessage('payment.notice.paragraph1')}
                    </NoticeParagraph>
                    <NoticeParagraph>
                        {TranslationHelpers.getFormattedMessage('payment.notice.paragraph2')}
                    </NoticeParagraph>
                </NoticeWrapper>
                {showCardSharingDisclaimer && (
                    <CardSharingDisclaimer
                        serviceName={cardSharingServiceName}
                        migrationDate={cardSharingMigrationDate}
                    />
                )}
                {main}
                {addCardsButton}
                <OverlayDialog
                    title={TranslationHelpers.getMessageDescriptor(
                        selectedCard ? 'payment.edit' : 'payment.new',
                    )}
                    isOpen={formOpen}
                    onClose={closeOverlay}
                    isLarge={true}
                >
                    <PaymentMethodFormContainer
                        card={selectedCard}
                        isOnlyCard={!cards || cards.length === 0}
                        onSubmit={() => {}}
                        showBillingAddress={showBillingAddress}
                        brandCodes={brandCodes}
                    />
                </OverlayDialog>
            </PaymentMethodsWrapper>
        );
    }

    private selectCard(card: ICard) {
        const { openOverlay } = this.props;
        this.setState({
            selectedCard: card,
        });
        openOverlay();
    }

    private createCard() {
        const { openOverlay } = this.props;
        addRatEvent('payment_method', 'add_card');
        this.setState({
            selectedCard: undefined,
        });
        openOverlay();
    }

    private addCardsButton(totalNumberOfCards: number, maxNumberOfCards: number): ReactNode {
        const buttonId = 'add-payment';
        const dataQaId = 'add-payment-method';
        const onClickEvent = this.createCard;
        const isPaymentNewLimitReached = totalNumberOfCards >= maxNumberOfCards;

        if (isPaymentNewLimitReached) {
            return (
                <TooltipWrapper
                    id="add-payment-tooltip"
                    tooltipText={TranslationHelpers.getMessageDescriptor(
                        'tooltip.payment.limit_reached',
                    )}
                    value={{ n: maxNumberOfCards.toString() }}
                >
                    <ButtonOutline
                        id={buttonId}
                        text={TranslationHelpers.getMessageDescriptor('payment.new_limit_reached')}
                        onClick={onClickEvent}
                        disabled={true}
                        data-qa-id={dataQaId}
                    />
                </TooltipWrapper>
            );
        } else {
            return (
                <ButtonOutline
                    id={buttonId}
                    text={TranslationHelpers.getMessageDescriptor('payment.new')}
                    icon={ICONS.PLUS}
                    onClick={onClickEvent}
                    disabled={false}
                    data-qa-id={dataQaId}
                />
            );
        }
    }
}

export default PaymentMethods;
